import { Layout } from '@community_dev/pixels'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Skeleton, Tooltip, Typography } from 'antd'
import { useState, type JSX } from 'react'
import styled from 'styled-components'

import { SYSTEMIC_ONBOARDING_STATE_LABELS } from '../General/constants'
import { CustomCheckboxModal } from '../General/CustomCheckboxModal'
import { useGeneralDetails } from '../General/queries'
import { isBlockingTexasAreaCodes } from '../General/utils'
import { SettingsCard, SettingsPage, SettingsRow, SettingsRows } from '../Settings'

import { Client, patchClientSettings, putClient, putContentReview } from 'api/clients'
import { AGE } from 'constants/age'
import { CAPABILITIES } from 'constants/capabilities'
import { QUERIES } from 'constants/queries'
import { useHasCapability } from 'hooks/useCapabilities'
import { useSystemicOnboardingQuery } from 'hooks/useSystemicOnboardingQuery'
import { InlineEditField } from 'shared/components/InlineEditField/InlineEditField'
import { Switch } from 'shared/components/Switch'

const StyledButton = styled(Button)`
  border: none;
  box-shadow: none;
`

const DEFAULT_TAG_LIMIT = 200
const MAX_TAG_LIMIT = 2000

export function MembersCompliance({ client }: { client: Client }): JSX.Element {
  const queryClient = useQueryClient()
  const [openLegalModal, setOpenLegalModal] = useState(false)
  const canUpdateTagLimit = useHasCapability(CAPABILITIES.ADMIN.TAG_LIMIT.WRITE)
  const canUpdateUnsupportedStateCodes = useHasCapability(CAPABILITIES.ADMIN.UNSUPPORTED_STATE_CODES.WRITE)
  const { isLoading, isError, contentReview } = useGeneralDetails(client.id)
  const texasRestrictionsEnabled = isBlockingTexasAreaCodes(client.unsupportedStateCodes)
  const { data: systemicOnboarding, isLoading: isSystemicOnboardingLoading } = useSystemicOnboardingQuery({
    clientId: client.id,
  })

  function invalidateClient() {
    queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
  }

  return (
    <SettingsPage
      description="Who can join, the limits on sub-communities and the compliance rules for this client."
      title="Members & Compliance"
    >
      <SettingsCard title="Members">
        <SettingsRows>
          <SettingsRow description="Raising the age can remove members below the new minimum" label="Minimum Fan Age">
            <InlineEditField
              defaultValue={client.minimumFanAge || AGE.MIN_AGE}
              name="Minimum Fan Age"
              onSuccess={invalidateClient}
              pattern={/^\d{0,3}$/}
              placeholder="13"
              prepare={(value) => ({
                id: client.id,
                body: { minimum_fan_age: parseInt(value) },
              })}
              updateEntity={putClient}
              validation={(value) => Number(value) >= AGE.MIN_AGE && Number(value) <= AGE.MAX_AGE}
              warningModalText="Changing the minimum fan age could trigger subscription deletions for members who fall below the new minimum age. Would you like to proceed?"
            />
          </SettingsRow>
          <SettingsRow
            description={`Default ${DEFAULT_TAG_LIMIT}, maximum ${MAX_TAG_LIMIT}`}
            label="Max Sub-Community Limit"
          >
            <InlineEditField
              defaultValue={client.tagLimit || DEFAULT_TAG_LIMIT}
              disabled={!canUpdateTagLimit}
              name="Max Sub-Community Limit"
              onSuccess={invalidateClient}
              pattern={/^\d{0,4}$/}
              placeholder={DEFAULT_TAG_LIMIT.toString()}
              prepare={(value) => ({
                id: client.id,
                body: { client_allotted_tag_limit: parseInt(value) },
              })}
              updateEntity={patchClientSettings}
              useErrorMessage={true}
              validation={(value) => Number(value) >= 1 && Number(value) <= MAX_TAG_LIMIT}
            />
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>

      <SettingsCard title="Compliance">
        <SettingsRows>
          <SettingsRow
            description="Prevent members with Texas area codes from opting in to comply with Texas SMS laws."
            label="Block Texas Area Codes"
          >
            <Switch
              defaultChecked={texasRestrictionsEnabled}
              disabled={!canUpdateUnsupportedStateCodes}
              name="Block Texas Area Codes"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { unsupported_state_codes: value ? ['TX'] : [] },
              })}
              updateEntity={patchClientSettings}
            />
          </SettingsRow>
          <SettingsRow description="Staff review campaigns before they send" label="Campaigns Require Review">
            <Skeleton active loading={isLoading} paragraph={false} title={{ width: 50 }} />
            {!isLoading && !isError && (
              <Switch
                defaultChecked={contentReview}
                name="Campaigns Review Required"
                onSuccess={invalidateClient}
                prepare={(value) => ({
                  id: client.id,
                  body: { content_review_required: value },
                })}
                updateEntity={putContentReview}
              />
            )}
          </SettingsRow>
          <SettingsRow description="Custom sign-up text and consent checkboxes" label="Additional legal language">
            <StyledButton aria-label="Edit additional legal language" onClick={() => setOpenLegalModal(true)}>
              <FontAwesomeIcon icon="edit" />
            </StyledButton>
          </SettingsRow>
          <SettingsRow
            description="Controls how many subscriptions can be created via the api."
            label={
              <>
                Systemic Onboarding Status
                <Tooltip
                  title={
                    <Layout display="flex" flexDirection="column">
                      <Layout marginBottom="12px">
                        <Typography.Text style={{ fontWeight: 'bold', color: 'white', marginBottom: '12px' }}>
                          Approved:{' '}
                        </Typography.Text>
                        <Typography.Text style={{ color: 'white' }}>
                          Allows creating unlimited subscriptions via the api.
                        </Typography.Text>
                      </Layout>
                      <Layout marginBottom="12px">
                        <Typography.Text style={{ fontWeight: 'bold', color: 'white' }}>Denied: </Typography.Text>
                        <Typography.Text style={{ color: 'white' }}>
                          No subscriptions can be created via the api.
                        </Typography.Text>
                      </Layout>
                      <Layout>
                        <Typography.Text style={{ fontWeight: 'bold', color: 'white' }}>
                          Awaiting Approval or Unset:{' '}
                        </Typography.Text>
                        <Typography.Text style={{ color: 'white' }}>
                          20 subscriptions per day and 60 total subscriptions.
                        </Typography.Text>
                      </Layout>
                    </Layout>
                  }
                >
                  <FontAwesomeIcon icon="info-circle" />
                </Tooltip>
              </>
            }
          >
            {isSystemicOnboardingLoading ? (
              <Skeleton active loading={isSystemicOnboardingLoading} paragraph={false} title={{ width: 50 }} />
            ) : (
              <Typography.Text>
                {SYSTEMIC_ONBOARDING_STATE_LABELS[systemicOnboarding?.data?.state] || ''}
              </Typography.Text>
            )}
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>

      <CustomCheckboxModal
        clientId={client.id}
        isOpen={openLegalModal}
        onCancel={() => setOpenLegalModal(false)}
        onDelete={() => setOpenLegalModal(false)}
        onSave={() => setOpenLegalModal(false)}
      />
    </SettingsPage>
  )
}
